import React from 'react';
import { DeckSlideWrapper } from '../DeckSlideWrapper';
import { Layers, Server, Cpu, Database, Binary, KeyRound } from 'lucide-react';

export const Slide04DeepTechMoat: React.FC = () => {
  return (
    <DeckSlideWrapper
      slideNumber={4}
      totalSlides={12}
      category="Deep Tech Moat"
      title="Engineering Excellence &amp; Architecture Purity"
      subtitle="Built from first principles for high-throughput enterprise security, verifiable correctness, and zero memory leaks."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3.5">
        {/* Pillar 1: Ephemeral In-Memory Token Vault */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <Binary className="w-3.5 h-3.5" />
              <span>01 // IN-MEMORY VAULT</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              Turn-Level DLP Tokenization
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              PII and credentials are replaced with surrogate tokens in RAM per HTTP turn and evicted after response deanonymization. Zero disk or log retention.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Latency overhead:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">&lt; 1.2ms RAM</span>
          </div>
        </div>

        {/* Pillar 2: Isolated V8 Sandbox & HITL */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <Cpu className="w-3.5 h-3.5" />
              <span>02 // V8 SANDBOXES</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              Isolated V8 &amp; HITL Dry-Run
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Cloudflare Dynamic Dispatch &amp; Deno runners execute agentic scripts safely. State mutations simulate in dryRun mode with visual diff cards before human commit.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Safety check:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">Two-Phase HITL Diff</span>
          </div>
        </div>

        {/* Pillar 3: Authoritative Secrets */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <KeyRound className="w-3.5 h-3.5" />
              <span>03 // SECRETS ARCHITECTURE</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              HashiCorp Vault &amp; Broker Proxy
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Zero raw credential ingress. Outbound calls route through Broker proxy so untrusted sandboxes and LLMs never touch unencrypted API keys or tokens.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Credential storage:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">HashiCorp Vault</span>
          </div>
        </div>

        {/* Pillar 4: WAP Connector Plugins */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <Layers className="w-3.5 h-3.5" />
              <span>04 // WAP PLUGINS</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              Workouse Action Protocol
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Standardized connector action runtime (`connectors.do`) connecting Paraşüt e-invoicing, CRM lifecycle, and OpenAPI 3.0 specs with type-safe schemas.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Ecosystem:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">Paraşüt, CRM, REST</span>
          </div>
        </div>

        {/* Pillar 5: Haskell Parsec Worker */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <Server className="w-3.5 h-3.5" />
              <span>05 // HASKELL WORKER</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              100% Async Document Parser
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              Deterministic Haskell Parsec queue worker extracts structured Markdown AST from enterprise PDFs and documents asynchronously with zero memory leaks.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Throughput:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">10k+ docs / min</span>
          </div>
        </div>

        {/* Pillar 6: Enterprise IAM & Edge DB */}
        <div className="p-3 sm:p-4 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1 sm:mb-1.5">
              <Database className="w-3.5 h-3.5" />
              <span>06 // ENTERPRISE IAM</span>
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white text-xs sm:text-sm mb-1">
              SAML 2.0, LDAP &amp; PostgREST Seam
            </h4>
            <p className="text-[11px] sm:text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              NextAuth v5 identity federation with post-login org selection, strict PostgreSQL Row-Level Security (RLS), and append-only cryptographic audit logging.
            </p>
          </div>
          <div className="mt-2.5 pt-2 border-t border-slate-200 dark:border-navy-700/60 font-mono text-[10px] sm:text-[11px] text-slate-500 flex items-center justify-between">
            <span>Isolation:</span>
            <span className="font-bold text-emerald-600 dark:text-emerald-400">Strict Tenant RLS</span>
          </div>
        </div>
      </div>
    </DeckSlideWrapper>
  );
};
